import React, { useState, useEffect } from 'react'
import { Row, Col, Space, Select } from 'antd'
import useInventory from '../../components/InventoryItems/hooks/useInventory'
import usePurchaseOrders from '../../components/PurchaseOrders/hooks/usePurchaseOrders'
import { getInventoryStatistics, getAlertItems, groupByField } from '../InventoryItems/utils/helpers'
import { DASHBOARD_PERIODS } from '../InventoryItems/utils/constants'
import '../../styles/InventoryDashboard.css'
import AddEditItemModal from '../InventoryItems/components/AddItemModal'
import DashboardHeader from './DashboardHeader'
import StatisticsCards from './StatisticsCards'
import InventoryCharts from './InventoryCharts'
import AlertsAndTables from './AlertsAndTables'
import { getStockStatus, getExpiryStatus, formatCurrency, formatDate } from '../InventoryItems/utils/helpers'
import { STOCK_STATUS, EXPIRY_STATUS } from '../InventoryItems/utils/constants'
import PurchaseOrderForm from '../PurchaseOrders/components/PurchaseOrderForm'

const { Option } = Select

const InventoryDashboard = () => {
  const { inventory, addInventoryItem, isAddModalVisible, showAddModal, hideAddModal } = useInventory()
  const {
    orders,
    isAddModalVisible: isPOModalVisible,
    showAddModal: showPOModal,
    hideAddModal: hidePOModal,
    createPurchaseOrder
  } = usePurchaseOrders()

  const [timeRange, setTimeRange] = useState('30d')
  const [categoryData, setCategoryData] = useState([])
  const [departmentData, setDepartmentData] = useState([])
  const [alertItems, setAlertItems] = useState([])
  const [pendingOrders, setPendingOrders] = useState([])
  const [stats, setStats] = useState({
    totalItems: 0,
    totalValue: 0,
    lowStockItems: 0,
    healthyStockItems: 0,
    expiringItems: 0,
    expiredItems: 0,
    pendingOrders: 0,
    ordersValue: 0
  })

  // Mock data for value trend chart
  const valueTrendData = [
    { name: 'Week 1', value: 42500 },
    { name: 'Week 2', value: 45000 },
    { name: 'Week 3', value: 43800 },
    { name: 'Week 4', value: 48500 },
    { name: 'Week 5', value: 47200 },
    { name: 'Week 6', value: 50000 }
  ]

  // Process data when inventory or orders change
  useEffect(() => {
    if (inventory.length > 0) {
      // Calculate inventory statistics
      const inventoryStats = getInventoryStatistics(inventory)

      // Get alerts for inventory items
      const alerts = getAlertItems(inventory)

      // Group inventory by category and department for charts
      const categoriesGrouped = groupByField(inventory, 'category')
      const departmentsGrouped = groupByField(inventory, 'department')

      // Calculate order statistics
      const pendingOrdersList = orders.filter((order) => order.status === 'Pending' || order.status === 'Approved')
      const ordersValue = pendingOrdersList.reduce((total, order) => total + order.totalCost, 0)

      // Update state with processed data
      setCategoryData(categoriesGrouped)
      setDepartmentData(departmentsGrouped)
      setAlertItems(alerts)
      setPendingOrders(pendingOrdersList)
      setStats({
        ...inventoryStats,
        pendingOrders: pendingOrdersList.length,
        ordersValue
      })
    }
  }, [inventory, orders])

  return (
    <div className='dashboard-container'>
      <DashboardHeader
        showAddModal={showAddModal}
        showPOModal={showPOModal}
        timeRange={timeRange}
        setTimeRange={setTimeRange}
        dashboardPeriods={DASHBOARD_PERIODS}
      />

      <div style={{ marginBottom: '24px' }}>
        <StatisticsCards stats={stats} />
      </div>

      <div style={{ marginBottom: '24px' }}>
        <InventoryCharts categoryData={categoryData} valueTrendData={valueTrendData} />
      </div>

      <AlertsAndTables alertItems={alertItems} pendingOrders={pendingOrders} departmentData={departmentData} />

      {/* Add Item Modal */}
      <AddEditItemModal
        visible={isAddModalVisible}
        onCancel={hideAddModal}
        onSave={addInventoryItem}
        title='Add Inventory Item'
        item={null}
      />

      {/* Import PurchaseOrderForm component and reuse it here */}
      {isPOModalVisible && (
        <React.Suspense fallback={<div>Loading...</div>}>
          {React.createElement(require('../PurchaseOrders/components/PurchaseOrderForm').default, {
            visible: isPOModalVisible,
            onCancel: hidePOModal,
            onSave: createPurchaseOrder,
            title: 'Create Purchase Order',
            initialValues: null
          })}
        </React.Suspense>
      )}
    </div>
  )
}

export default InventoryDashboard
